<template>
  <Teleport to="body">
    <transition name="agegate-fade">
      <div
        v-if="visible"
        class="agegate"
        role="dialog"
        aria-modal="true"
        aria-labelledby="agegate-title"
      >
        <div class="agegate__panel" @click.stop>
          <div class="agegate__logo">🤠</div>
          <h2 id="agegate-title" class="agegate__title">
            Are you 21 or older, partner?
          </h2>
          <p class="agegate__subtitle">
            Banjino's is a craft Texas wine spritz. We have to ask.
          </p>

          <div class="agegate__actions">
            <button class="agegate__btn agegate__btn--yes" @click="confirm">
              Yes, let me through
            </button>
            <a class="agegate__btn agegate__btn--no" href="https://www.responsibility.org">
              No, take me back
            </a>
          </div>

          <p class="agegate__legal">
            By entering this site, you certify you are of legal drinking age. Please drink responsibly.
          </p>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script setup>
import { ref, onMounted } from 'vue'

const STORAGE_KEY = 'banjino_age_verified_at'
const TTL_DAYS = 30

const visible = ref(false)

const isVerified = () => {
  try {
    const v = localStorage.getItem(STORAGE_KEY)
    if (!v) return false
    const ts = Number(v)
    if (Number.isNaN(ts)) return false
    const ageMs = Date.now() - ts
    return ageMs < TTL_DAYS * 86400 * 1000
  } catch (_) {
    return false
  }
}

const confirm = () => {
  try { localStorage.setItem(STORAGE_KEY, String(Date.now())) } catch (_) { /* private mode */ }
  visible.value = false
  document.body.style.overflow = ''
}

onMounted(() => {
  if (!isVerified()) {
    visible.value = true
    document.body.style.overflow = 'hidden'
  }
})
</script>

<style lang="scss" scoped>
.agegate {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(20, 14, 8, 0.92);
  backdrop-filter: blur(8px);
  padding: 1.25rem;

  &__panel {
    width: 100%;
    max-width: 540px;
    padding: 2.5rem 2rem;
    background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
    border: 3px solid #d8b767;
    border-radius: 20px;
    box-shadow:
      0 24px 80px rgba(0, 0, 0, 0.8),
      inset 0 1px 0 rgba(255, 255, 255, 0.05);
    text-align: center;
    color: #f4e5c1;
  }

  &__logo {
    font-size: 4rem;
    line-height: 1;
    margin-bottom: 1.25rem;
    filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.4));
  }

  &__title {
    font-family: $font-heading;
    font-size: clamp(1.5rem, 4vw, 2.25rem);
    font-weight: 400;
    line-height: 1.2;
    margin: 0 0 0.5rem;
    color: #f4e5c1;
    text-shadow: 2px 2px 6px rgba(0, 0, 0, 0.6);
    letter-spacing: 0.02em;
  }

  &__subtitle {
    font-family: $font-ui;
    font-size: 1rem;
    margin: 0 0 2rem;
    color: #e0c890;
    font-style: italic;
    opacity: 0.9;
  }

  &__actions {
    display: flex;
    flex-direction: column;
    gap: 0.85rem;
    margin: 0 0 1.5rem;

    @media (min-width: 480px) {
      flex-direction: row;
      justify-content: center;
    }
  }

  &__btn {
    flex: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-family: $font-ui;
    font-weight: 700;
    font-size: 0.9rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    text-decoration: none;
    padding: 0.95rem 1.5rem;
    border-radius: 12px;
    border: 2px solid transparent;
    cursor: pointer;
    transition: all 200ms ease-out;
    min-width: 160px;

    &--yes {
      background: linear-gradient(180deg, #285338 0%, #1f4329 100%);
      color: #ffffff;
      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 10px 24px rgba(40, 83, 56, 0.5);
        border-color: #d8b767;
      }
    }

    &--no {
      background: linear-gradient(180deg, #912e1a 0%, #6b2113 100%);
      color: #ffffff;
      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 10px 24px rgba(145, 46, 26, 0.5);
      }
    }

    &:focus-visible {
      outline: 3px solid #d8b767;
      outline-offset: 2px;
    }
  }

  &__legal {
    font-family: $font-ui;
    font-size: 0.78rem;
    color: #d8b767;
    margin: 0;
    opacity: 0.75;
    line-height: 1.5;
  }
}

.agegate-fade-enter-active,
.agegate-fade-leave-active {
  transition: opacity 200ms ease;
}
.agegate-fade-enter-from,
.agegate-fade-leave-to {
  opacity: 0;
}
</style>
